import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  Briefcase,
  CheckCircle as CheckCircle2,
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
  Eye,
  EnvelopeSimple as Mail,
  DotsThreeVertical as MoreVertical,
  Phone,
  Plus,
  MagnifyingGlass as Search,
  ShareNetwork as Share2,
  Trash as Trash2,
  UserCircle as UserRound,
  Users,
  X,
} from "@phosphor-icons/react";
import {
  prospects as prospectsApi,
  documents as documentsApi,
} from "@/api";
import ShareModal from "@/components/ShareModal";
import OpportunityCreateModal from "@/components/OpportunityCreateModal";
import { useAuth } from "@/context/AuthContext";
import {
  ActaActionMenu,
  ActaActionMenuItem,
} from "@/components/ui/ActaActionMenu";
import ActaModal from "@/components/ui/ActaModal";
import ActaStandardPage from "@/components/layout/ActaStandardPage";
import CommerceMetricCard from "@/components/commerce/CommerceMetricCard";
import ContactCallChannelSheet from "@/components/commerce/ContactCallChannelSheet";
import CallReturnDialog from "@/components/commerce/CallReturnDialog";
import PurchaseIntentBadge, { PURCHASE_INTENT_OPTIONS } from "@/components/commerce/PurchaseIntentBadge";
import ContactFormFields, {
  getContactFormValidationError,
  isContactFormValid,
} from "@/components/commerce/ContactFormFields";
import usePendingCallSession from "@/hooks/usePendingCallSession";
import { whatsappUrl } from "@/lib/contactChannels";
import "./CommercePortfolio.acta.css";

// ACTA_COMMERCE_PORTFOLIO_SAAS_V1
// ACTA_COMMERCE_PORTFOLIO_MOBILE_POLISH_V2
// ACTA_CONTACTS_RAPIDES_MOBILE_V1
// ACTA_CONTACTS_MOBILE_ACTION_FIRST_V2
// ACTA_MOBILE_CONTACT_DIRECTORY_V1
// ACTA_CONTACTS_INTERACTIVE_UI_V1
// ACTA_CONTACTS_APPROVED_DESKTOP_MOBILE_V2
// ACTA_CONTACTS_SECURE_MOBILE_SHARE_3KPI_V3



const SORT_OPTIONS = [
  { value: "prospectName", label: "Contact" },
  { value: "company", label: "Société" },
  { value: "lifecycleStage", label: "Statut" },
  { value: "purchaseIntent", label: "Intention d’achat" },
  { value: "ownerName", label: "Commercial" },
  { value: "city", label: "Ville" },
  { value: "contact", label: "Coordonnées" },
  { value: "lastActivityAt", label: "Dernière activité" },
  { value: "nextActionAt", label: "Prochaine action" },
  { value: "createdAt", label: "Création" },
];


const emptyPersonaForm = {
  firstName: "",
  lastName: "",
  company: "",
  email: "",
  phone: "",
  position: "",
  address: "",
  postalCode: "",
  city: "",
  country: "FR",
  lat: null,
  lng: null,
  lifecycleStage: "prospect",
};

function normalizePersona(raw) {
  const lifecycleStage =
    raw?.lifecycleStage === "client" ||
      raw?.status === "client"
      ? "client"
      : "prospect";

  const firstName =
    raw?.firstName ||
    raw?.first_name ||
    "";

  const lastName =
    raw?.lastName ||
    raw?.last_name ||
    "";

  return {
    ...raw,
    id: raw?.id || raw?._id,
    firstName,
    lastName,
    name:
      raw?.name ||
      `${firstName} ${lastName}`.trim() ||
      raw?.contactName ||
      raw?.contact ||
      raw?.company ||
      "Sans nom",
    company:
      raw?.company ||
      raw?.companyName ||
      raw?.organization ||
      "-",
    email:
      raw?.email ||
      raw?.contactEmail ||
      "",
    phone:
      raw?.phone ||
      raw?.contactPhone ||
      "",
    address:
      typeof raw?.address === "string"
        ? raw.address
        : "",
    city:
      raw?.city ||
      raw?.address?.city ||
      "",
    ownerName:
      raw?.ownerName ||
      raw?.assignedUserName ||
      raw?.commercialName ||
      "",
    assignedUserId:
      raw?.assignedUserId ||
      raw?.assigned_user_id ||
      "",
    commercialStatus:
      raw?.commercialStatus ||
      raw?.commercial_status ||
      "lead",
    acquisitionLeadId:
      raw?.acquisitionLeadId ||
      raw?.acquisition_lead_id ||
      "",
    lifecycleStage,
    purchaseIntent:
      raw?.purchaseIntent ||
      raw?.purchase_intent ||
      "",
    lastActivityAt:
      raw?.lastActivityAt ||
      raw?.updatedAt ||
      raw?.createdAt ||
      "",
    nextActionAt:
      raw?.nextActionAt ||
      raw?.nextActionDate ||
      "",
  };
}

function extractItems(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.prospects)) {
    return response.prospects;
  }
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.results)) {
    return response.results;
  }
  return [];
}

function readNumber(...values) {
  for (const value of values) {
    const number = Number(value);

    if (Number.isFinite(number)) {
      return number;
    }
  }

  return 0;
}

function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("fr-FR");
}

function cleanAmount(value) {
  const amount = Number(value || 0);

  return Number.isFinite(amount) && amount >= 0
    ? amount
    : 0;
}

function initials(persona) {
  const letters = [
    persona?.firstName,
    persona?.lastName,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim().charAt(0))
    .join("")
    .slice(0, 2);

  if (letters) return letters.toUpperCase();

  return String(
    persona?.company ||
    persona?.name ||
    "P",
  )
    .trim()
    .charAt(0)
    .toUpperCase();
}

export default function CommercePortfolio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedAction = searchParams.get("action");
  const requestedEmail = searchParams.get("email") || "";
  const requestedReturnTo = searchParams.get("returnTo") || "";

  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] =
    useState("");
  const [statusFilter, setStatusFilter] =
    useState("all");
  const [commercialFilter, setCommercialFilter] =
    useState("");
  const [purchaseIntentFilter, setPurchaseIntentFilter] = useState("");
  const [commercialUsers, setCommercialUsers] =
    useState([]);

  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [tableTotal, setTableTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [portfolioSummary, setPortfolioSummary] = useState({
    total: 0,
    prospects: 0,
    clients: 0,
  });

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [formError, setFormError] = useState("");
  const [deleteError, setDeleteError] = useState("");

  const [personaModalOpen, setPersonaModalOpen] =
    useState(false);
  const [personaForm, setPersonaForm] =
    useState(emptyPersonaForm);
  const [savingPersona, setSavingPersona] =
    useState(false);

  const [
    opportunityModalOpen,
    setOpportunityModalOpen,
  ] = useState(false);
  const [selectedPersonaId, setSelectedPersonaId] =
    useState("");
  const [documents, setDocuments] =
    useState([]);
  const [documentsLoading, setDocumentsLoading] =
    useState(false);
  const [documentsError, setDocumentsError] =
    useState("");

  const [docPickerPersona, setDocPickerPersona] =
    useState(null);

  const [shareItem, setShareItem] =
    useState(null);
  const [sharePersona, setSharePersona] =
    useState(null);
  const [deletingId, setDeletingId] =
    useState("");

  const [
    mobileFiltersOpen,
    setMobileFiltersOpen,
  ] = useState(false);

  const [
    mobileActionPersona,
    setMobileActionPersona,
  ] = useState(null);

  const [desktopActionPersona, setDesktopActionPersona] =
    useState(null);
  const [desktopActionAnchor, setDesktopActionAnchor] =
    useState(null);
  const [callPersona, setCallPersona] = useState(null);
  const [callChannelOpen, setCallChannelOpen] = useState(false);
  const [callSaving, setCallSaving] = useState(false);

  const callSession =
    usePendingCallSession();

  const requestIdRef = useRef(0);
  const resultsRef = useRef(null);
  const documentsLoadedRef = useRef(false);

  const canFilterCommercials = [
    "admin",
    "manager",
  ].includes(user?.role);

  function canDeletePersona(persona) {
    return (
      user?.role === "admin" ||
      (
        Boolean(persona?.assignedUserId) &&
        persona.assignedUserId === user?.id
      )
    );
  }

  function clearActionUrl() {
    navigate("/app/commerce/prospects", {
      replace: true,
    });
  }


  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    async function loadCommercialUsers() {
      if (
        !canFilterCommercials ||
        typeof prospectsApi.listCommercialUsers !==
        "function"
      ) {
        setCommercialUsers([]);
        return;
      }

      try {
        const response =
          await prospectsApi.listCommercialUsers();

        setCommercialUsers(
          Array.isArray(response)
            ? response.filter((item) => item?.id)
            : [],
        );
      } catch (error) {
        if (error?.response?.status !== 403) {
          console.warn(
            "Chargement commerciaux impossible",
            error,
          );
        }

        setCommercialUsers([]);
      }
    }

    loadCommercialUsers();
  }, [canFilterCommercials]);


  const loadContactSummary = useCallback(async () => {
    if (typeof prospectsApi.contactSummary !== "function") {
      return;
    }

    try {
      const response = await prospectsApi.contactSummary({
        assignedUserId: commercialFilter,
      });

      setPortfolioSummary({
        total: Number(response?.total || 0),
        prospects: Number(response?.prospects || 0),
        clients: Number(response?.clients || 0),
      });
    } catch (error) {
      console.warn("Chargement KPI Contacts impossible", error);
    }
  }, [commercialFilter]);

  useEffect(() => {
    loadContactSummary();
  }, [loadContactSummary]);

  const loadPortfolio = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading(true);
    setLoadError("");
    setDeleteError("");

    try {
      const response =
        typeof prospectsApi.paginated === "function"
          ? await prospectsApi.paginated({
            page,
            limit: pageSize,
            search: debouncedQuery,
            assignedUserId: commercialFilter,
            lifecycleStage: statusFilter,
            purchaseIntent: purchaseIntentFilter,
            dateFilter: "all",
            sortBy,
            sortOrder,
            view: "contacts",
          })
          : await prospectsApi.list();

      if (requestId !== requestIdRef.current) {
        return;
      }

      const normalizedItems = extractItems(response)
        .map(normalizePersona)
        .filter((persona) => persona.id);

      const responseTotal = readNumber(
        response?.total,
        response?.count,
        response?.pagination?.total,
        normalizedItems.length,
      );

      const responseTotalPages = Math.max(
        1,
        readNumber(
          response?.totalPages,
          response?.pages,
          response?.pagination?.totalPages,
          Math.ceil(responseTotal / pageSize),
        ),
      );

      const responsePage = Math.max(
        1,
        readNumber(
          response?.page,
          response?.pagination?.page,
          page,
        ),
      );

      setItems(normalizedItems);
      setTableTotal(responseTotal);
      setTotalPages(responseTotalPages);

      if (responsePage !== page) {
        setPage(responsePage);
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setItems([]);
      setTableTotal(0);
      setTotalPages(1);
      setLoadError(
        error?.response?.data?.detail ||
        error?.message ||
        "Impossible de charger le portefeuille.",
      );
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    commercialFilter,
    purchaseIntentFilter,
    debouncedQuery,
    page,
    pageSize,
    sortBy,
    sortOrder,
    statusFilter,
  ]);

  useEffect(() => {
    loadPortfolio();
  }, [loadPortfolio]);

  useEffect(() => {
    if (requestedAction === "new-persona") {
      openPersonaModal(requestedEmail);
    }

    if (requestedAction === "new-opportunity") {
      openOpportunityModal();
    }
  }, [requestedAction, requestedEmail]);

  // ACTA_PORTFOLIO_DESKTOP_ACTIONS_REMOVED_STEP1_V1
  // ACTA_PORTFOLIO_GLOBAL_COUNTERS_V1
  const totals = useMemo(() => ({
    total: portfolioSummary.total,
    prospects: portfolioSummary.prospects,
    clients: portfolioSummary.clients,
  }), [portfolioSummary]);

  function openPersonaModal(prefilledEmail = "") {
    const safePrefilledEmail =
      typeof prefilledEmail === "string"
        ? prefilledEmail.trim()
        : "";

    setPersonaForm({
      ...emptyPersonaForm,
      email: safePrefilledEmail,
    });
    setPersonaModalOpen(true);
    setFormError("");
  }

  function closePersonaModal() {
    setPersonaModalOpen(false);
    setFormError("");

    if (requestedAction === "new-persona") {
      clearActionUrl();
    }
  }

  function openOpportunityModal(persona = null) {
    setSelectedPersonaId(
      persona?.id || ""
    );
    setOpportunityModalOpen(true);
    setFormError("");
  }

  function closeOpportunityModal() {
    setOpportunityModalOpen(false);
    setFormError("");

    if (requestedAction === "new-opportunity") {
      clearActionUrl();
    }
  }

  async function resolvePersonaAddress() {
    const address = personaForm.address.trim();
    const postalCode = personaForm.postalCode;
    const city = personaForm.city;

    if (!address) {
      return;
    }

    try {
      const resolved =
        await prospectsApi.resolveLocation(
          address,
        );

      setPersonaForm((current) => {
        if (
          current.address.trim() !== address ||
          current.postalCode !== postalCode ||
          current.city !== city
        ) {
          return current;
        }

        const resolvedPostalCode = String(
          resolved?.postalCode || "",
        ).trim();
        const resolvedCity = String(
          resolved?.city || "",
        ).trim();

        if (!resolvedPostalCode && !resolvedCity) {
          return current;
        }

        return {
          ...current,
          postalCode:
            resolvedPostalCode || current.postalCode,
          city: resolvedCity || current.city,
          country: String(
            resolved?.country || current.country || "FR",
          ).trim().toUpperCase(),
          lat: resolved?.lat ?? null,
          lng: resolved?.lng ?? null,
        };
      });
    } catch (error) {
      console.warn(
        "Resolution de l'adresse impossible",
        error,
      );
    }
  }

  async function createPersona(event) {
    event.preventDefault();

    const validationError =
      getContactFormValidationError(
        personaForm,
      );

    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSavingPersona(true);
    setFormError("");

    try {
      const created = await prospectsApi.create({
        first_name:
          personaForm.firstName.trim() ||
          "Contact",
        last_name:
          personaForm.lastName.trim(),
        company: personaForm.company.trim(),
        email: personaForm.email.trim(),
        phone: personaForm.phone.trim(),
        position: personaForm.position.trim(),
        address: personaForm.address.trim(),
        postal_code:
          personaForm.postalCode.trim(),
        city: personaForm.city.trim(),
        country:
          personaForm.country || "FR",
        lat: personaForm.lat,
        lng: personaForm.lng,
        commercial_status: "lead",
      });

      if (
        personaForm.lifecycleStage === "client" &&
        created?.id &&
        typeof prospectsApi.convertToClient ===
        "function"
      ) {
        await prospectsApi.convertToClient(
          created.id,
        );
      }

      setPersonaModalOpen(false);

      if (requestedReturnTo) {
        navigate(requestedReturnTo, {
          replace: true,
        });
      } else {
        clearActionUrl();
      }

      setPage(1);
      await Promise.all([
        loadPortfolio(),
        loadContactSummary(),
      ]);
    } catch (error) {
      setFormError(
        error?.response?.data?.detail ||
        error?.message ||
        "Impossible de créer le persona.",
      );
    } finally {
      setSavingPersona(false);
    }
  }

  async function deletePersona(persona) {
    if (!canDeletePersona(persona)) {
      setDeleteError(
        "Seul le propriétaire du contact ou un administrateur peut le supprimer.",
      );
      return;
    }

    setDeletingId(persona.id);
    setDeleteError("");

    try {
      const impact =
        await prospectsApi.deleteImpact(
          persona.id,
        );

      const dependencies =
        Array.isArray(
          impact?.dependencies,
        )
          ? impact.dependencies
          : [];

      const dependencyMessage =
        dependencies.length
          ? `\n\nÉléments liés qui seront archivés :\n${dependencies
            .map(
              (dependency) =>
                `• ${dependency.count} ${dependency.label}`,
            )
            .join("\n")}`
          : "";

      const contactLabel =
        persona.company !== "-"
          ? persona.company
          : persona.name;

      const confirmed = window.confirm(
        `Supprimer définitivement ${contactLabel} du portefeuille ?${dependencyMessage}`,
      );

      if (!confirmed) {
        return;
      }

      await prospectsApi.remove(
        persona.id,
      );

      if (
        items.length === 1 &&
        page > 1
      ) {
        setPage((current) =>
          Math.max(
            1,
            current - 1,
          ),
        );

        await loadContactSummary();
      } else {
        await Promise.all([
          loadPortfolio(),
          loadContactSummary(),
        ]);
      }
    } catch (error) {
      setDeleteError(
        error?.response?.data?.detail ||
        error?.message ||
        "Impossible de supprimer ce contact.",
      );
    } finally {
      setDeletingId("");
    }
  }

  function changeStatus(value) {
    setStatusFilter(value);
    setPage(1);
  }

  function changeCommercial(value) {
    setCommercialFilter(value);
    setPage(1);
  }

  function changeSort(field) {
    if (!SORT_OPTIONS.some((option) => option.value === field)) {
      return;
    }

    if (sortBy === field) {
      setSortOrder((current) =>
        current === "asc" ? "desc" : "asc"
      );
    } else {
      setSortBy(field);
      setSortOrder(
        field === "lastActivityAt" || field === "createdAt"
          ? "desc"
          : "asc"
      );
    }

    setPage(1);
  }

  async function startPortfolioCall(
    channel
  ) {
    if (
      !callPersona?.id ||
      !callPersona?.phone ||
      callSaving
    ) {
      return;
    }

    setCallSaving(true);

    try {
      const activity =
        await prospectsApi.startCall(
          callPersona.id,
          channel,
          "portfolio_contacts"
        );

      callSession.remember(
        activity,
        callPersona
      );

      setCallChannelOpen(false);

      window.location.href =
        channel === "whatsapp"
          ? whatsappUrl(
              callPersona.phone,
              ""
            )
          : `tel:${callPersona.phone}`;

      await loadPortfolio();
    } catch (error) {
      setLoadError(
        error?.response?.data?.detail ||
        error?.message ||
        "Impossible de démarrer l’appel."
      );
    } finally {
      setCallSaving(false);
    }
  }

  async function savePortfolioCallOutcome(
    resultCode
  ) {
    if (
      !callSession.pending?.activity?.id ||
      callSaving
    ) {
      return false;
    }

    setCallSaving(true);

    try {
      await prospectsApi.recordCallOutcome(
        callSession.pending.activity.id,
        resultCode
      );

      callSession.markResolved();
      await loadPortfolio();
      return true;
    } catch (error) {
      setLoadError(
        error?.response?.data?.detail ||
        error?.message ||
        "Impossible d’enregistrer le résultat de l’appel."
      );
      return false;
    } finally {
      setCallSaving(false);
    }
  }

  function navigateFromCall(
    action
  ) {
    const prospectId =
      callSession.pending?.contact?.id ||
      "";

    if (!prospectId) {
      callSession.finish();
      return;
    }

    callSession.finish();

    if (action === "followup") {
      navigate(
        `/app/commerce/agenda?action=new-followup&prospectId=${encodeURIComponent(
          prospectId
        )}`
      );
      return;
    }

    if (action === "new-action") {
      navigate(
        `/app/commerce/agenda?action=new-action&prospectId=${encodeURIComponent(
          prospectId
        )}`
      );
      return;
    }

    navigate(
      `/app/commerce/prospects/${encodeURIComponent(
        prospectId
      )}?tab=information`
    );
  }

  function openCallReport(persona) {
    if (!persona?.id) return;

    navigate(
      `/app/commerce/prospects/${persona.id}` +
      "?action=new-activity&activityType=call",
    );
  }

  function openDesktopActionMenu(event, persona) {
    event.stopPropagation();

    if (desktopActionPersona?.id === persona.id) {
      setDesktopActionPersona(null);
      setDesktopActionAnchor(null);
      return;
    }

    setDesktopActionPersona(persona);
    setDesktopActionAnchor(event.currentTarget);
  }

  function closeDesktopActionMenu() {
    setDesktopActionPersona(null);
    setDesktopActionAnchor(null);
  }

  async function openDocumentShare(persona) {
    setDocPickerPersona(persona);
    setSharePersona(persona);
    setShareItem(null);
    closeDesktopActionMenu();

    if (
      documentsLoadedRef.current ||
      documentsLoading
    ) {
      return;
    }

    setDocumentsLoading(true);
    setDocumentsError("");

    try {
      const docs = await documentsApi.list();

      setDocuments(
        Array.isArray(docs) ? docs : [],
      );

      documentsLoadedRef.current = true;
    } catch (error) {
      setDocuments([]);
      setDocumentsError(
        error?.response?.data?.detail ||
        error?.message ||
        "Impossible de charger les documents.",
      );
    } finally {
      setDocumentsLoading(false);
    }
  }

  function selectShareDocument(document) {
    setShareItem({
      id: document.id,
      type: "document",
      name:
        document.name ||
        document.filename ||
        document.title ||
        "Document",
      items: [
        {
          type: "document",
          id: document.id,
        },
      ],
    });
    setDocPickerPersona(null);
  }

  function closeDocumentShare() {
    setDocPickerPersona(null);
    setSharePersona(null);
    setShareItem(null);
  }

  async function shareContact(persona) {
    const contactLines = [
      persona?.name,
      persona?.company && persona.company !== "-"
        ? persona.company
        : "",
      persona?.phone || "",
      persona?.email || "",
      persona?.address || persona?.city || "",
    ].filter(Boolean);

    const text = contactLines.join("\n");

    try {
      if (navigator.share) {
        await navigator.share({
          title: persona?.name || "Contact",
          text,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        window.alert("Coordonnées copiées.");
        return;
      }

      window.prompt(
        "Copiez les coordonnées du contact :",
        text,
      );
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.warn("Partage du contact impossible", error);
      }
    }
  }

  const selectedPersona = items.find(
    (item) => item.id === selectedPersonaId,
  );

  return (
    <ActaStandardPage className="acta-portfolio-page acta-contacts-page">

      <section className="acta-portfolio-stats acta-contacts-desktop-kpis">
        <CommerceMetricCard
          label="Contacts totaux"
          value={totals.total}
          detail="Tous les contacts"
          icon={Users}
          active={statusFilter === "all"}
          onClick={() => changeStatus("all")}
          testId="contacts-kpi-all"
        />

        <CommerceMetricCard
          label="Prospects"
          value={totals.prospects}
          detail="À convertir"
          icon={UserRound}
          active={statusFilter === "prospect"}
          onClick={() => changeStatus("prospect")}
          testId="contacts-kpi-prospects"
        />

        <CommerceMetricCard
          label="Clients"
          value={totals.clients}
          detail="Clients actifs"
          icon={CheckCircle2}
          active={statusFilter === "client"}
          onClick={() => changeStatus("client")}
          testId="contacts-kpi-clients"
        />
      </section>



      <section className="acta-contacts-desktop-toolbar">


        <label className="acta-contacts-desktop-search">

          <Search size={18} />

          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher un contact"
          />

        </label>



        {canFilterCommercials ? (
          <label className="acta-contacts-owner-filter">
            <span className="sr-only">
              Filtrer par commercial
            </span>

            <select
              value={commercialFilter}
              onChange={(event) =>
                changeCommercial(event.target.value)
              }
            >
              <option value="">
                {user?.role === "manager"
                  ? "Mon équipe"
                  : "Tous les commerciaux"}
              </option>

              {commercialUsers.map((commercial) => (
                <option
                  key={commercial.id}
                  value={commercial.id}
                >
                  {[
                    commercial.firstName,
                    commercial.lastName,
                  ]
                    .filter(Boolean)
                    .join(" ") ||
                    commercial.name ||
                    commercial.email ||
                    "Commercial"}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label className="acta-contacts-owner-filter">
          <span className="sr-only">Filtrer par intention d’achat</span>
          <select value={purchaseIntentFilter} onChange={(event) => { setPurchaseIntentFilter(event.target.value); setPage(1); }}>
            <option value="">Toutes intentions</option>
            {PURCHASE_INTENT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <button
          type="button"
          className="acta-primary-button acta-contacts-desktop-new"
          onClick={() => openPersonaModal()}
        >

          <Plus size={17} />

          Nouveau contact

        </button>



      </section>

      <section className="acta-contacts-mobile-toolbar">
        <label className="acta-contacts-mobile-search">
          <Search size={18} />

          <input
            value={query}
            onChange={(event) =>
              setQuery(event.target.value)
            }
            placeholder="Rechercher un contact"
          />
        </label>

        <button
          type="button"
          className="acta-primary-button acta-contacts-mobile-new"
          onClick={() => openPersonaModal()}
        >
          <Plus size={17} />
          Nouveau
        </button>
      </section>

      <section className="acta-contacts-mobile-toolbar" aria-label="Filtre d’intention d’achat">
        <label className="acta-contacts-owner-filter"><span className="sr-only">Filtrer par intention d’achat</span><select value={purchaseIntentFilter} onChange={(event) => { setPurchaseIntentFilter(event.target.value); setPage(1); }}><option value="">Toutes intentions</option>{PURCHASE_INTENT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
      </section>
      {loadError ? (
        <div
          className="acta-portfolio-error"
          role="alert"
        >
          <span>{loadError}</span>

          <button
            type="button"
            onClick={loadPortfolio}
          >
            Réessayer
          </button>
        </div>
      ) : null}

      {deleteError ? (
        <div
          className="acta-portfolio-error"
          role="alert"
        >
          {deleteError}
        </div>
      ) : null}

      <section
        className="acta-portfolio-table-card"
        aria-busy={loading}
      >


        <table className="acta-portfolio-table">

          <thead>

            <tr>

              <th aria-sort={sortBy === "prospectName" ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="acta-contacts-sort-button" onClick={() => changeSort("prospectName")} aria-label="Trier par Contact">{"Contact"}{sortBy === "prospectName" ? (sortOrder === "asc" ? " ↑" : " ↓") : null}</button></th>
              <th aria-sort={sortBy === "company" ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="acta-contacts-sort-button" onClick={() => changeSort("company")} aria-label="Trier par Société">{"Société"}{sortBy === "company" ? (sortOrder === "asc" ? " ↑" : " ↓") : null}</button></th>
              <th aria-sort={sortBy === "lifecycleStage" ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="acta-contacts-sort-button" onClick={() => changeSort("lifecycleStage")} aria-label="Trier par Statut">{"Statut"}{sortBy === "lifecycleStage" ? (sortOrder === "asc" ? " ↑" : " ↓") : null}</button></th>
              <th aria-sort={sortBy === "purchaseIntent" ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="acta-contacts-sort-button" onClick={() => changeSort("purchaseIntent")} aria-label="Trier par Intention d’achat">{"Intention d’achat"}{sortBy === "purchaseIntent" ? (sortOrder === "asc" ? " ↑" : " ↓") : null}</button></th>
              <th aria-sort={sortBy === "ownerName" ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="acta-contacts-sort-button" onClick={() => changeSort("ownerName")} aria-label="Trier par Commercial">{"Commercial"}{sortBy === "ownerName" ? (sortOrder === "asc" ? " ↑" : " ↓") : null}</button></th>
              <th aria-sort={sortBy === "lastActivityAt" ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="acta-contacts-sort-button" onClick={() => changeSort("lastActivityAt")} aria-label="Trier par Dernière activité">{"Dernière activité"}{sortBy === "lastActivityAt" ? (sortOrder === "asc" ? " ↑" : " ↓") : null}</button></th>
              <th aria-sort={sortBy === "nextActionAt" ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="acta-contacts-sort-button" onClick={() => changeSort("nextActionAt")} aria-label="Trier par Prochaine action">{"Prochaine action"}{sortBy === "nextActionAt" ? (sortOrder === "asc" ? " ↑" : " ↓") : null}</button></th>
              <th>Actions</th>

            </tr>

          </thead>


          <tbody>


            {loading && items.length === 0 ? (
              <tr>
                <td
                  colSpan="8"
                  className="acta-contacts-table-state"
                >
                  Chargement des contacts…
                </td>
              </tr>
            ) : null}

            {!loading && !loadError && items.length === 0 ? (
              <tr>
                <td
                  colSpan="8"
                  className="acta-contacts-table-state"
                >
                  Aucun contact ne correspond à votre recherche.
                </td>
              </tr>
            ) : null}

            {items.map((persona) => (

              <tr key={persona.id}>


                <td>

                  <Link
                    to={`/app/commerce/prospects/${persona.id}`}
                    className="acta-persona-cell"
                  >


                    <span className="acta-persona-icon">

                      {initials(persona)}

                    </span>


                    <span>

                      <strong>
                        {persona.name}
                      </strong>


                      <small>
                        {persona.email || persona.phone || "-"}
                      </small>


                    </span>


                  </Link>

                </td>



                <td>
                  {persona.company}
                </td>



                <td>

                  <span
                    className={`acta-status-badge is-${persona.lifecycleStage}`}
                  >

                    {
                      persona.lifecycleStage === "client"
                        ? "Client"
                        : persona.acquisitionLeadId && persona.commercialStatus === "lead"
                          ? "Nouveau lead"
                          : "Prospect"
                    }

                  </span>


                </td>

                <td>
                  <PurchaseIntentBadge
                    value={persona.purchaseIntent}
                  />
                </td>

                <td>

                  {persona.ownerName || "-"}

                </td>



                <td>

                  {formatDate(persona.lastActivityAt)}

                </td>



                <td>

                  {formatDate(persona.nextActionAt)}

                </td>



                <td>


                  <div className="acta-portfolio-desktop-actions">


                    {persona.phone ? (
                      <button
                        type="button"
                        className="acta-portfolio-icon-action"
                        aria-label={`Appeler ${persona.name}`}
                        onClick={() => {
                          setCallPersona(persona);
                          setCallChannelOpen(true);
                        }}
                      >
                        <Phone size={16} />
                      </button>
                    ) : (
                      <span
                        className="acta-portfolio-icon-action is-disabled"
                        aria-hidden="true"
                      >
                        <Phone size={16} />
                      </span>
                    )}

                    {persona.email ? (
                      <a
                        href={`mailto:${persona.email}`}
                        className="acta-portfolio-icon-action"
                        aria-label={`Écrire à ${persona.name}`}
                      >
                        <Mail size={16} />
                      </a>
                    ) : (
                      <span
                        className="acta-portfolio-icon-action is-disabled"
                        aria-hidden="true"
                      >
                        <Mail size={16} />
                      </span>
                    )}



                    <button
                      className="acta-portfolio-icon-action"
                      onClick={(event) =>
                        openDesktopActionMenu(event, persona)
                      }
                    >

                      <MoreVertical size={17} />

                    </button>


                  </div>


                </td>



              </tr>


            ))}


          </tbody>


        </table>

        <div className="acta-contacts-approved-mobile-list">
          {loading && items.length === 0 ? (
            <div className="acta-contacts-mobile-state">
              Chargement des contacts…
            </div>
          ) : null}

          {!loading && !loadError && items.length === 0 ? (
            <div className="acta-contacts-mobile-state">
              Aucun contact ne correspond à votre recherche.
            </div>
          ) : null}

          {items.map((persona) => (
            <article
              key={persona.id}
              className="acta-contacts-approved-mobile-card"
            >
              <Link
                to={`/app/commerce/prospects/${persona.id}`}
                className="acta-contacts-mobile-identity"
              >
                <span className="acta-persona-icon">
                  {initials(persona)}
                </span>

                <span className="acta-contacts-mobile-details">
                  <strong>{persona.name}</strong>

                  <span className="acta-contacts-mobile-company">
                    {persona.company !== "-"
                      ? persona.company
                      : persona.lifecycleStage === "client"
                        ? "Client"
                        : persona.acquisitionLeadId && persona.commercialStatus === "lead"
                          ? "Nouveau lead"
                          : "Prospect"}
                  </span>

                  <PurchaseIntentBadge
                    value={persona.purchaseIntent}
                    className="mt-1"
                  />

                  {persona.phone ? (
                    <span className="acta-contacts-mobile-coordinate">
                      <Phone size={13} />
                      {persona.phone}
                    </span>
                  ) : null}

                  {persona.email ? (
                    <span className="acta-contacts-mobile-coordinate">
                      <Mail size={13} />
                      {persona.email}
                    </span>
                  ) : null}
                </span>
              </Link>

              <div className="acta-contacts-mobile-primary-actions">
                {persona.phone ? (
                  <button
                    type="button"
                    className="acta-contacts-mobile-call"
                    onClick={() => {
                      setCallPersona(persona);
                      setCallChannelOpen(true);
                    }}
                  >
                    <Phone size={15} />
                    Appeler
                  </button>
                ) : null}

                {persona.email ? (
                  <a
                    href={`mailto:${persona.email}`}
                    className="acta-contacts-mobile-email"
                  >
                    <Mail size={15} />
                    Email
                  </a>
                ) : null}

                <button
                  type="button"
                  className="acta-contacts-mobile-share"
                  onClick={() =>
                    shareContact(persona)
                  }
                >
                  <Share2 size={15} />
                  Partager
                </button>
              </div>
            </article>
          ))}
        </div>

        <footer className="acta-contacts-compact-pagination">

          <span className="acta-contacts-pagination-summary">
            {tableTotal === 0
              ? "0 contact"
              : `${(page - 1) * pageSize + 1}–${Math.min(
                  tableTotal,
                  page * pageSize,
                )} sur ${tableTotal}`}
          </span>

          <button
            type="button"
            disabled={page <= 1 || loading}
            aria-label="Page précédente"
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >

            <ChevronLeft size={17} />

          </button>


          <strong>
            {page} / {totalPages}
          </strong>


          <button
            type="button"
            disabled={page >= totalPages || loading}
            aria-label="Page suivante"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          >

            <ChevronRight size={17} />

          </button>


        </footer>


      </section>

      <ActaActionMenu
        open={Boolean(
          desktopActionPersona &&
          desktopActionAnchor,
        )}
        anchorEl={desktopActionAnchor}
        onClose={closeDesktopActionMenu}
        width={245}
        estimatedHeight={250}
        testId="contacts-desktop-action-menu"
      >
        {desktopActionPersona ? (
          <>
            <ActaActionMenuItem
              icon={<Eye size={16} />}
              label="Voir la fiche"
              description="Consulter le contact"
              onClick={() => {
                const persona =
                  desktopActionPersona;
                closeDesktopActionMenu();
                navigate(
                  `/app/commerce/prospects/${persona.id}`,
                );
              }}
              testId={`contacts-desktop-view-${desktopActionPersona.id}`}
            />

            <ActaActionMenuItem
              icon={<Briefcase size={16} />}
              label="Créer une opportunité"
              description="Ajouter au pipeline"
              onClick={() => {
                const persona =
                  desktopActionPersona;
                closeDesktopActionMenu();
                openOpportunityModal(persona);
              }}
              testId={`contacts-desktop-opportunity-${desktopActionPersona.id}`}
            />

            <ActaActionMenuItem
              icon={<Share2 size={16} />}
              label="Partager le contact"
              description="Partager ses coordonnées"
              onClick={() => {
                const persona =
                  desktopActionPersona;
                closeDesktopActionMenu();
                shareContact(persona);
              }}
              testId={`contacts-desktop-share-contact-${desktopActionPersona.id}`}
            />

            <ActaActionMenuItem
              icon={<CheckCircle2 size={16} />}
              label="Compte rendu d'appel"
              description="Renseigner l'échange"
              onClick={() => {
                const persona =
                  desktopActionPersona;
                closeDesktopActionMenu();
                openCallReport(persona);
              }}
              testId={`contacts-desktop-call-report-${desktopActionPersona.id}`}
            />

            {canDeletePersona(
              desktopActionPersona,
            ) ? (
              <ActaActionMenuItem
                icon={<Trash2 size={16} />}
                label="Supprimer le contact"
                description="Retirer la fiche et archiver les éléments liés"
                danger
                disabled={
                  deletingId ===
                  desktopActionPersona.id
                }
                onClick={() => {
                  const persona =
                    desktopActionPersona;
                  closeDesktopActionMenu();
                  deletePersona(persona);
                }}
                testId={`contacts-desktop-delete-${desktopActionPersona.id}`}
              />
            ) : null}
          </>
        ) : null}
      </ActaActionMenu>

      {docPickerPersona ? (
        <div
          className="acta-modal-backdrop"
          role="presentation"
          onClick={closeDocumentShare}
          data-testid="contacts-document-picker"
        >
          <section
            className="acta-opportunity-modal acta-contacts-document-picker"
            role="dialog"
            aria-modal="true"
            aria-label={`Partager un document avec ${docPickerPersona.name}`}
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="acta-modal-header">
              <div>
                <p className="label-eyebrow">
                  Partager un document
                </p>
                <h2>Choisir un document</h2>
                <p>
                  Le partage sera préparé pour{" "}
                  {docPickerPersona.company !== "-"
                    ? docPickerPersona.company
                    : docPickerPersona.name}
                  .
                </p>
              </div>

              <button
                type="button"
                onClick={closeDocumentShare}
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="acta-contacts-document-picker-list">
              {documentsLoading ? (
                <div className="acta-portfolio-mobile-empty">
                  Chargement des documents…
                </div>
              ) : documentsError ? (
                <div
                  className="acta-portfolio-error"
                  role="alert"
                >
                  {documentsError}
                </div>
              ) : documents.length ? (
                documents.map((document) => (
                  <button
                    key={document.id}
                    type="button"
                    className="acta-contacts-document-picker-item"
                    onClick={() =>
                      selectShareDocument(document)
                    }
                    data-testid={`contacts-select-document-${document.id}`}
                  >
                    <Share2 size={17} />
                    <span>
                      <strong>
                        {document.name ||
                          document.filename ||
                          document.title ||
                          "Document"}
                      </strong>
                      <small>
                        {document.mimeType ||
                          document.extension ||
                          "Document"}
                      </small>
                    </span>
                  </button>
                ))
              ) : (
                <div className="acta-portfolio-mobile-empty">
                  Aucun document disponible.
                </div>
              )}
            </div>
          </section>
        </div>
      ) : null}

      {shareItem ? (
        <ShareModal
          item={shareItem}
          defaultProspect={sharePersona}
          onClose={closeDocumentShare}
        />
      ) : null}

      <ActaModal
        open={personaModalOpen}
        onClose={closePersonaModal}
        eyebrow="Nouveau contact"
        title="Créer un prospect ou client"
        subtitle="Un persona porte uniquement le statut Prospect ou Client."
        size="md"
        testId="contacts-create-modal"
        footer={
          <div className="acta-modal-actions">
            <button
              type="button"
              className="acta-secondary-button"
              onClick={closePersonaModal}
            >
              Annuler
            </button>

            <button
              type="submit"
              form="contacts-create-form"
              className="acta-primary-button"
              disabled={
                savingPersona ||
                !isContactFormValid(
                  personaForm
                )
              }
            >
              <Users size={16} />
              Créer le contact
            </button>
          </div>
        }
      >
        <form
          id="contacts-create-form"
          onSubmit={createPersona}
        >
          {formError ? (
            <div className="acta-portfolio-error">
              {formError}
            </div>
          ) : null}

          <ContactFormFields
            value={personaForm}
            onChange={setPersonaForm}
            onAddressBlur={resolvePersonaAddress}
          />
        </form>
      </ActaModal>

      <OpportunityCreateModal
        open={opportunityModalOpen}
        prospects={items}
        defaultProspect={items.find((item) => item.id === selectedPersonaId) || null}
        onClose={closeOpportunityModal}
        onCreated={async () => {
          clearActionUrl();
          await loadPortfolio();
        }}
      />

      <CallReturnDialog
        open={callSession.returnOpen}
        contact={callSession.pending?.contact}
        saving={callSaving}
        onOutcome={savePortfolioCallOutcome}
        onFollowup={() => navigateFromCall("followup")}
        onNewAction={() => navigateFromCall("new-action")}
        onNote={() => navigateFromCall("note")}
        onFinish={callSession.finish}
        onClose={callSession.closeReturn}
        testId="contacts-call-return"
      />

      <ContactCallChannelSheet
        open={callChannelOpen}
        phone={callPersona?.phone}
        saving={callSaving}
        onClose={() => setCallChannelOpen(false)}
        onSelect={startPortfolioCall}
        testIdPrefix="contacts"
      />

    </ActaStandardPage>
  );
}
